import React, { useState, useMemo } from 'react';
import './GenButton.css';
import { AutoAwesome, Close, East, Stop } from '@mui/icons-material';
import useAsymptoticGauge, { gaugePhaseFromStatus } from '../../hooks/useAsymptoticGauge';

const ARIA_GEN_BUTTON = { fr: 'G\u00e9n\u00e9rer', en: 'Generate', de: 'Generieren', es: 'Generar', pt: 'Gerar' };
const ARIA_STOP_BUTTON = { fr: 'Arreter', en: 'Stop', de: 'Stoppen', es: 'Detener', pt: 'Parar' };
const BTN_LABEL_IDLE = { fr: 'G\u00e9n\u00e9rer', en: 'Generate', de: 'Generieren', es: 'Generar', pt: 'Gerar' };
const BTN_LABEL_STOP = { fr: 'Arr\u00eater', en: 'Stop', de: 'Stoppen', es: 'Detener', pt: 'Parar' };
const TXT_IDLE_NO_CRITERIA = { fr: 'S\u00e9lectionnez des crit\u00e8res', en: 'Select criteria', de: 'Kriterien auswählen', es: 'Selecciona criterios', pt: 'Selecione critérios' };

const TXT_INITIALIZING = { fr: 'Initialisation...', en: 'Initializing...', de: 'Initialisierung...', es: 'Inicializando...', pt: 'Inicializando...' };
const TXT_QUEUED = { fr: 'En file d\'attente...', en: 'Queued...', de: 'In der Warteschlange...', es: 'En cola...', pt: 'Na fila...' };
const TXT_BOOTING = { fr: 'D\u00e9marrage du solveur...', en: 'Starting solver...', de: 'Solver startet...', es: 'Iniciando el solver...', pt: 'Iniciando o solver...' };
const TXT_GENERATING = { fr: 'G\u00e9n\u00e9ration...', en: 'Generating...', de: 'Generierung...', es: 'Generando...', pt: 'Gerando...' };
const TXT_FINALIZING = { fr: 'Finalisation...', en: 'Finalizing...', de: 'Abschluss...', es: 'Finalizando...', pt: 'Finalizando...' };

const Spinner = () => (
    // Purely decorative: the button already carries aria-label + aria-busy.
    <div className="gen-spinner" aria-hidden="true">
        <svg className="gen-spinner-svg" width="20" height="20" viewBox="0 0 50 50" aria-hidden="true">
            <circle
                className="gen-spinner-ring"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#ffffff"
                strokeWidth="5"
                strokeDasharray="31.4 31.4"
                strokeLinecap="round"
            />
        </svg>
    </div>
);

const GenButton = ({
    onClick,
    onStop,
    status,
    disabled,
    hasTargets = true,
    invalidBuildReason = null,
    lang = 'fr',
    timeLimit = 30,
    localSolve = false,
    forceStopVisual = false,
    noIcon = false,
    noIdleIcon = false,
    iconOnly = false,
}) => {
    const [isHover, setIsHover] = useState(false);

    const isIdle = status === 'idle';
    const isInitializing = status === 'initializing';
    const isQueued = status === 'queued';
    const isBooting = status === 'booting';
    const isLoading = status === 'loading';
    const isBusy = !isIdle;
    const isFinalizing = status === 'finalizing';
    const isGaugeActive = isQueued || isBooting || isInitializing || isLoading || isFinalizing;
    const gaugeRef = useAsymptoticGauge(
        isGaugeActive,
        timeLimit,
        gaugePhaseFromStatus(status),
        localSolve,
    );
    const noTargets = !hasTargets;
    const hasInvalidBuild = typeof invalidBuildReason === 'string' && invalidBuildReason.trim().length > 0;
    const showInvalidBuildState = isIdle && hasInvalidBuild;

    const isStopAction = isBusy && !!onStop;
    const showStopVisual = isStopAction && (forceStopVisual || isHover);

    const statusLabel = useMemo(() => {
        if (!isBusy) return BTN_LABEL_IDLE[lang] || BTN_LABEL_IDLE.en;
        if (isInitializing) return TXT_INITIALIZING[lang] || TXT_INITIALIZING.en;
        if (isQueued) return TXT_QUEUED[lang] || TXT_QUEUED.en;
        if (isBooting) return TXT_BOOTING[lang] || TXT_BOOTING.en;
        if (isFinalizing) return TXT_FINALIZING[lang] || TXT_FINALIZING.en;
        return TXT_GENERATING[lang] || TXT_GENERATING.en;
    }, [isBusy, isInitializing, isQueued, isBooting, isFinalizing, lang]);

    const label = isIdle
        ? (
            showInvalidBuildState
                ? invalidBuildReason
                : (noTargets
                    ? (TXT_IDLE_NO_CRITERIA[lang] || TXT_IDLE_NO_CRITERIA.en)
                    : (BTN_LABEL_IDLE[lang] || BTN_LABEL_IDLE.en))
        )
        : showStopVisual
            ? (BTN_LABEL_STOP[lang] || BTN_LABEL_STOP.en)
            : statusLabel;

    const effectiveDisabled = isBusy
        ? !onStop
        : disabled || noTargets || showInvalidBuildState;

    const ariaLabel = isStopAction
        ? (ARIA_STOP_BUTTON[lang] || ARIA_STOP_BUTTON.en)
        : showInvalidBuildState
            ? invalidBuildReason
            : isIdle && noTargets
                ? (TXT_IDLE_NO_CRITERIA[lang] || TXT_IDLE_NO_CRITERIA.en)
                : (ARIA_GEN_BUTTON[lang] || ARIA_GEN_BUTTON.en);

    const handleClick = (e) => {
        e.preventDefault();
        if (isBusy && onStop) {
            onStop();
        } else if (isIdle && !effectiveDisabled) {
            onClick();
        }
    };

    const renderIcon = () => {
        if (iconOnly) {
            if (!isBusy) return <East fontSize="small" />;
            if (showStopVisual) return <Stop fontSize="small" />;
            return <Spinner />;
        }
        if (noIcon) return null;
        if (isIdle && (noTargets || showInvalidBuildState)) return null;
        if (isIdle) return noIdleIcon ? null : <AutoAwesome fontSize="small" />;
        if (showStopVisual) return <Close fontSize="small" />;
        return <Spinner />;
    };

    return (
        <div
            className={`gen-button row ${showStopVisual ? 'gen-button--stop' : ''}${showInvalidBuildState ? ' gen-button--invalid' : ''}${noIcon ? ' gen-button--no-icon' : ''}${iconOnly ? ' gen-button--icon-only' : ''}`}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <button
                className="primary"
                onClick={handleClick}
                disabled={effectiveDisabled}
                aria-label={ariaLabel}
                aria-busy={isBusy ? 'true' : undefined}
            >
                {isGaugeActive && (
                    <div className="gen-button-gauge" ref={gaugeRef} />
                )}
                <div
                    className={`row center gen-btn-content btn-content${isIdle && (noTargets || showInvalidBuildState) ? ' gen-btn-content--no-criteria' : ''}`}
                >
                    {(iconOnly || (!noIcon && !(isIdle && noTargets) && !(isIdle && noIdleIcon))) && (
                        <span className="gen-btn-icon btn-icon">
                            {renderIcon()}
                        </span>
                    )}
                    {!iconOnly ? (
                        <div className="gen-btn-text-stack column">
                            {isIdle && (noTargets || showInvalidBuildState) ? (
                                <span className="gen-btn-hint">{label}</span>
                            ) : (
                                <h4 className="gen-btn-title btn-label">{label}</h4>
                            )}
                        </div>
                    ) : null}
                </div>
            </button>
        </div>
    );
};

export default GenButton;
